/**
 * Redux duck for managing all user handles
 * Fetches all handles from Sharetribe and stores them for uniqueness checking
 */

import { storableError } from '../util/errors';
import { apiBaseUrl } from '../util/api';

// Action types
const FETCH_HANDLES_REQUEST = 'app/userHandles/FETCH_HANDLES_REQUEST';
const FETCH_HANDLES_SUCCESS = 'app/userHandles/FETCH_HANDLES_SUCCESS';
const FETCH_HANDLES_ERROR = 'app/userHandles/FETCH_HANDLES_ERROR';

// Initial state
const initialState = {
  handles: [], // Array of all handles in the system
  fetchInProgress: false,
  fetchError: null,
  lastFetched: null,
};

// Reducer
export default function userHandlesReducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case FETCH_HANDLES_REQUEST:
      return {
        ...state,
        fetchInProgress: true,
        fetchError: null,
      };

    case FETCH_HANDLES_SUCCESS:
      return {
        ...state,
        handles: payload.handles,
        fetchInProgress: false,
        lastFetched: new Date().getTime(),
      };

    case FETCH_HANDLES_ERROR:
      return {
        ...state,
        fetchInProgress: false,
        fetchError: payload,
      };

    default:
      return state;
  }
}

// Action creators
export const fetchHandlesRequest = () => ({ type: FETCH_HANDLES_REQUEST });
export const fetchHandlesSuccess = handles => ({
  type: FETCH_HANDLES_SUCCESS,
  payload: { handles },
});
export const fetchHandlesError = error => ({
  type: FETCH_HANDLES_ERROR,
  payload: error,
  error: true,
});

// Selectors
export const getAllHandles = state => state.userHandles.handles;
export const isHandleTaken = (state, handle) => {
  const normalizedHandle = handle.toLowerCase().trim();
  return state.userHandles.handles.some(h => h.toLowerCase().trim() === normalizedHandle);
};

// Thunks
export const fetchAllUserHandles = () => (dispatch, getState, sdk) => {
  dispatch(fetchHandlesRequest());

  // Call backend API to get all handles
  return fetch(`${apiBaseUrl()}/api/handles`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  })
    .then(response => {
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        return response.text().then(text => {
          throw new Error(
            `Expected JSON from /api/handles, received ${contentType || 'unknown content type'}: ${text.slice(
              0,
              120
            )}`
          );
        });
      }
      return response.json();
    })
    .then(data => {
      if (data.success) {
        const handles = data.handles || [];
        dispatch(fetchHandlesSuccess(handles));
        return handles;
      } else {
        throw new Error(data.message || 'Failed to fetch handles');
      }
    })
    .catch(error => {
      console.error('❌ Error fetching user handles:', error);
      dispatch(fetchHandlesError(storableError(error)));
    });
};
